import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Pill, Plus, Check, X, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function MedicationsSection({ language }) {
    const [meds, setMeds] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [newMed, setNewMed] = useState({ name: '', dosage: '', category: 'supplement', frequency: 'daily' });
    const [loading, setLoading] = useState(false);
    const [autoFilling, setAutoFilling] = useState(false);
    const t = language === 'de';

    useEffect(() => { loadMeds(); }, []);

    const loadMeds = async () => {
        try {
            const res = await axios.get(`${API}/api/health/medications/today`);
            setMeds(Array.isArray(res.data) ? res.data : []);
        } catch (e) { console.error(e); }
    };

    const handleAdd = async () => {
        if (!newMed.name.trim()) return;
        setLoading(true);
        try {
            await axios.post(`${API}/api/health/medications`, { ...newMed });
            toast.success(`${newMed.name} ${t ? 'hinzugefügt' : 'added'}`);
            setNewMed({ name: '', dosage: '', category: 'supplement', frequency: 'daily' });
            setShowAdd(false);
            loadMeds();
        } catch (e) { toast.error('Error'); }
        finally { setLoading(false); }
    };

    const handleTake = async (medId, medName) => {
        try {
            await axios.post(`${API}/api/health/medications/log`, { medication_id: medId });
            toast.success(`${medName} ${t ? 'genommen' : 'taken'}`);
            loadMeds();
        } catch (e) { toast.error('Error'); }
    };

    const handleDelete = async (medId) => {
        try {
            await axios.delete(`${API}/api/health/medications/${medId}`);
            loadMeds();
        } catch (e) { toast.error('Error'); }
    };

    const handleAutoFill = async () => {
        setAutoFilling(true);
        try {
            const res = await axios.post(`${API}/api/health/medications/autofill`, { days: 30 });
            toast.success(res.data.message);
        } catch (e) { toast.error('Error'); }
        finally { setAutoFilling(false); }
    };

    const taken = meds.filter(m => m.taken_today).length;
    const total = meds.length;

    return (
        <Card className="border" data-testid="medications-section">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Pill className="w-4 h-4 text-violet-500" />
                        <h3 className="font-semibold text-sm">{t ? 'Medikamente & Supplements' : 'Medications & Supplements'}</h3>
                        {total > 0 && <Badge variant="outline" className="text-[10px]">{taken}/{total}</Badge>}
                    </div>
                    <div className="flex gap-1">
                        {total > 0 && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleAutoFill} disabled={autoFilling} title="Auto-Fill 30 Tage">
                                {autoFilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowAdd(!showAdd)}>
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Add new medication */}
                {showAdd && (
                    <div className="mb-3 p-3 rounded-xl bg-muted/50 border border-border space-y-2">
                        <Input
                            value={newMed.name}
                            onChange={(e) => setNewMed({...newMed, name: e.target.value})}
                            placeholder={t ? 'Name (z.B. Vitamin D, Omega-3)' : 'Name (e.g. Vitamin D, Omega-3)'}
                            className="bg-card border-2 border-border h-9 text-sm"
                            data-testid="med-name-input"
                        />
                        <div className="flex gap-2">
                            <Input
                                value={newMed.dosage}
                                onChange={(e) => setNewMed({...newMed, dosage: e.target.value})}
                                placeholder={t ? 'Dosierung (z.B. 2000 IE)' : 'Dosage (e.g. 2000 IU)'}
                                className="bg-card border-2 border-border h-9 text-sm flex-1"
                            />
                            <select
                                value={newMed.category}
                                onChange={(e) => setNewMed({...newMed, category: e.target.value})}
                                className="h-9 px-2 rounded-md border-2 border-border bg-card text-xs"
                            >
                                <option value="supplement">{t ? 'Supplement' : 'Supplement'}</option>
                                <option value="vitamin">Vitamin</option>
                                <option value="medication">{t ? 'Medikament' : 'Medication'}</option>
                                <option value="hormone">{t ? 'Hormon' : 'Hormone'}</option>
                            </select>
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={newMed.frequency || 'daily'}
                                onChange={(e) => setNewMed({...newMed, frequency: e.target.value})}
                                className="h-9 px-2 rounded-md border-2 border-border bg-card text-xs flex-1"
                            >
                                <option value="daily">{t ? 'Täglich' : 'Daily'}</option>
                                <option value="twice_daily">{t ? '2x täglich' : 'Twice daily'}</option>
                                <option value="weekly">{t ? 'Wöchentlich' : 'Weekly'}</option>
                                <option value="as_needed">{t ? 'Bei Bedarf' : 'As needed'}</option>
                            </select>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleAdd} disabled={loading || !newMed.name.trim()} className="flex-1 gap-1">
                                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                {t ? 'Speichern' : 'Save'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>
                                <X className="w-3 h-3" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Medication list with take buttons */}
                {meds.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-3">
                        {t ? 'Keine Medikamente definiert. Tippe + zum Hinzufügen.' : 'No medications defined. Tap + to add.'}
                    </p>
                ) : (
                    <div className="space-y-1.5">
                        {meds.map((med) => (
                            <div key={med.id} className="flex items-center gap-2 group">
                                <button
                                    onClick={() => !med.taken_today && handleTake(med.id, med.name)}
                                    disabled={med.taken_today}
                                    className={`w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                                        med.taken_today
                                            ? 'bg-green-500 border-green-500 text-white'
                                            : 'border-border hover:border-primary hover:bg-primary/10'
                                    }`}
                                >
                                    {med.taken_today && <Check className="w-4 h-4" />}
                                </button>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${med.taken_today ? 'line-through text-muted-foreground' : ''}`}>{med.name}</p>
                                    <p className="text-[10px] text-muted-foreground">
                                        {med.dosage && `${med.dosage} • `}
                                        <button
                                            onClick={async () => {
                                                const freqs = ['daily', 'twice_daily', 'weekly', 'as_needed'];
                                                const labels = t ? ['Täglich', '2x täglich', 'Wöchentlich', 'Bei Bedarf'] : ['Daily', 'Twice daily', 'Weekly', 'As needed'];
                                                const current = freqs.indexOf(med.frequency || 'daily');
                                                const next = (current + 1) % freqs.length;
                                                try {
                                                    await axios.put(`${API}/api/health/medications/${med.id}`, { name: med.name, frequency: freqs[next] });
                                                    toast.success(`${med.name}: ${labels[next]}`);
                                                    loadMeds();
                                                } catch(e) { toast.error('Error'); }
                                            }}
                                            className="underline decoration-dotted hover:text-primary"
                                        >
                                            {med.frequency === 'daily' ? (t ? 'Täglich' : 'Daily') : 
                                             med.frequency === 'twice_daily' ? (t ? '2x täglich' : '2x daily') :
                                             med.frequency === 'weekly' ? (t ? 'Wöchentlich' : 'Weekly') :
                                             (t ? 'Bei Bedarf' : 'As needed')}
                                        </button>
                                    </p>
                                </div>
                                <Badge variant="outline" className="text-[9px] shrink-0">{med.category}</Badge>
                                <button onClick={() => handleDelete(med.id)} className="opacity-0 group-hover:opacity-100 p-1">
                                    <X className="w-3 h-3 text-muted-foreground" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
